import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Progress } from "../components/ui/progress";
import { LayoutDashboard, Dumbbell, Play, TrendingUp, Calendar, LogOut, Clock, CheckCircle, Target, ChevronRight, FileText, FolderOpen, Download, ExternalLink } from "lucide-react";
import BrandMark from "../components/BrandMark";
import { toast } from "sonner";
import ScheduleCalendar from "../components/ScheduleCalendar";

const tabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "training", label: "Training", icon: Dumbbell },
  { id: "progress", label: "Progress", icon: TrendingUp },
  { id: "updates", label: "Updates", icon: FileText },
  { id: "documents", label: "Documents", icon: FolderOpen },
  { id: "schedule", label: "Schedule", icon: Calendar },
];

const DAYS = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" },
];

export default function ClientDashboard() {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("overview");
  const [data, setData] = useState(null);
  const [clientNotes, setClientNotes] = useState([]);
  const [clientDocs, setClientDocs] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data: d } = await api.get("/dashboard/client");
      setData(d);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  useEffect(() => {
    // Guard against stale responses if the user switches tabs rapidly.
    let cancelled = false;
    if (activeTab === "updates") {
      api.get("/notes").then(({ data: n }) => { if (!cancelled) setClientNotes(n); }).catch((err) => console.warn("Load notes failed", err));
    }
    if (activeTab === "documents") {
      api.get("/documents").then(({ data: d }) => { if (!cancelled) setClientDocs(d); }).catch((err) => console.warn("Load docs failed", err));
    }
    return () => { cancelled = true; };
    // State setters are stable by React guarantee; `api` is a module singleton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const formatDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return d; } };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" /></div>;

  const prog = data.program;
  const programAssessments = data.program_assessments || [];
  const baselineAssessment = programAssessments.find(a => a.phase === "baseline");
  const latestAssessment = programAssessments.length ? programAssessments[programAssessments.length - 1] : null;

  // Build per-metric summary from the program's baseline assessment (names as source of truth)
  const progressMetrics = (baselineAssessment?.metrics || []).map(bm => {
    const history = programAssessments.map(a => {
      const m = (a.metrics || []).find(x => x.name === bm.name);
      return m ? { date: formatDate(a.date), phase: a.phase, value: m.value, left: m.left_value, right: m.right_value, status: m.status } : null;
    }).filter(Boolean);
    const latest = latestAssessment ? (latestAssessment.metrics || []).find(x => x.name === bm.name) : null;
    return {
      name: bm.name,
      unit: bm.unit,
      direction: bm.direction,
      baseline: { value: bm.value, left: bm.left_value, right: bm.right_value },
      target: { value: bm.target_value, left: bm.target_left, right: bm.target_right },
      latest: latest ? { value: latest.value, left: latest.left_value, right: latest.right_value, status: latest.status } : null,
      history,
    };
  });

  const pctProgress = (baseline, current, target) => {
    if (baseline == null || current == null || target == null || baseline === target) return null;
    const pct = ((current - baseline) / (target - baseline)) * 100;
    return Math.max(0, Math.min(100, Math.round(pct)));
  };

  const renderContent = () => {
    switch (activeTab) {
      case "overview": {
        const activeMilestones = (data.milestones || []).filter(m => prog && m.program_id === prog.id);
        const achievedCount = activeMilestones.filter(m => m.achieved).length;
        const totalCount = activeMilestones.length;
        const progressPct = totalCount > 0 ? Math.round((achievedCount / totalCount) * 100) : 0;
        return (
        <div className="space-y-6" data-testid="client-overview">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={Target} label="Current Program" value={prog?.title || "No active program"} sub={prog ? `Week ${Math.ceil((Date.now() - new Date(prog.start_date)) / 604800000)} of ${prog.duration_weeks}` : ""} />
            <StatCard icon={Calendar} label="Upcoming Sessions" value={data.upcoming_sessions?.length || 0} sub="Scheduled" />
            <StatCard icon={CheckCircle} label="Milestones" value={`${achievedCount}/${totalCount}`} sub="Achieved" />
          </div>
          {prog && (
            <div className="bg-card border border-border p-6" data-testid="client-progress-card">
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <h3 className="font-bold">{prog.title}</h3>
                <span className="px-2 py-0.5 bg-[#007AFF]/10 text-[#007AFF] font-bold text-[10px]">{prog.status?.replace("_", " ").toUpperCase()}</span>
              </div>
              {prog.objective && <p className="text-sm text-muted-foreground mb-4">{prog.objective}</p>}
              {/* Progress bar driven by milestones, not visits */}
              <div className="mb-5">
                <div className="flex items-baseline justify-between mb-1">
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Progress</p>
                  <p className="text-xs text-muted-foreground">{achievedCount} of {totalCount} milestones achieved {totalCount > 0 && `· ${progressPct}%`}</p>
                </div>
                {totalCount > 0
                  ? <Progress value={progressPct} className="h-2" data-testid="client-milestone-progress" />
                  : <p className="text-xs text-muted-foreground italic">No milestones have been set for this program yet.</p>}
              </div>
              {/* Active-program milestones — name only, click to expand */}
              {activeMilestones.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Milestones</p>
                  <div className="divide-y divide-border border border-border">
                    {activeMilestones.map(m => <MilestoneRow key={m.id} milestone={m} formatDate={formatDate} />)}
                  </div>
                </div>
              )}
            </div>
          )}
          {data.upcoming_sessions?.length > 0 && (
            <div className="bg-card border border-border p-6">
              <h3 className="font-bold mb-4">Upcoming Sessions</h3>
              <div className="space-y-3">
                {data.upcoming_sessions.slice(0, 3).map(s => (
                  <div key={s.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-3">
                      <Clock className="w-4 h-4 text-[#007AFF]" />
                      <div>
                        <p className="text-sm font-medium">{s.type.replace("_", " ")} Session</p>
                        <p className="text-xs text-muted-foreground">{s.notes}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">{formatDate(s.date)}</p>
                      <p className="text-xs text-muted-foreground">{s.time} ({s.duration_minutes}min)</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ); }
      case "training": {
        const activeProgram = prog;
        const programAssignments = activeProgram
          ? (data.assignments || []).filter(a => a.program_id === activeProgram.id)
          : [];
        // group by day of week
        const byDay = DAYS.map(d => ({
          ...d,
          items: programAssignments
            .filter(a => (a.day_of_week || []).map(x => x.toLowerCase()).includes(d.key))
            .sort((x, y) => (x.order || 99) - (y.order || 99)),
        })).filter(d => d.items.length > 0);
        const unscheduled = programAssignments.filter(a => !(a.day_of_week || []).length);

        if (!activeProgram) {
          return (
            <div className="space-y-4" data-testid="client-training">
              <div className="bg-card border border-border p-8 text-center">
                <Dumbbell className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-bold text-lg mb-2">No active training program</h3>
                <p className="text-muted-foreground text-sm">Otto will build your first program after your baseline assessment. Check back soon.</p>
              </div>
            </div>
          );
        }
        const weeksElapsed = Math.max(1, Math.ceil((Date.now() - new Date(activeProgram.start_date)) / 604800000));
        const totalExercises = programAssignments.length;

        return (
          <div className="space-y-6" data-testid="client-training">
            {/* Program header */}
            <div className="bg-card border border-border p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#007AFF] mb-1">Current Program</p>
                  <h3 className="font-black text-2xl tracking-tight">{activeProgram.title}</h3>
                  {activeProgram.objective && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{activeProgram.objective}</p>}
                </div>
                <div className="flex items-center gap-3 text-xs flex-shrink-0">
                  <div className="text-right">
                    <p className="text-muted-foreground">Week</p>
                    <p className="font-black text-lg">{Math.min(weeksElapsed, activeProgram.duration_weeks)} <span className="text-muted-foreground font-normal text-sm">/ {activeProgram.duration_weeks}</span></p>
                  </div>
                  <div className="h-10 w-px bg-border" />
                  <div className="text-right">
                    <p className="text-muted-foreground">Exercises</p>
                    <p className="font-black text-lg">{totalExercises}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sessions by day */}
            {byDay.length === 0 && unscheduled.length === 0 ? (
              <div className="bg-card border border-border p-8 text-center">
                <p className="text-sm text-muted-foreground">No exercises assigned to this program yet.</p>
              </div>
            ) : (
              <>
                {byDay.map(day => (
                  <div key={day.key} className="space-y-3" data-testid={`training-day-${day.key}`}>
                    <div className="flex items-baseline justify-between border-b-2 border-[#007AFF] pb-2">
                      <h4 className="font-black text-lg tracking-tight">{day.label} <span className="text-muted-foreground font-normal text-sm">· Session</span></h4>
                      <span className="text-xs text-muted-foreground">{day.items.length} exercise{day.items.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {day.items.map((a, idx) => <ExerciseCard key={a.id} a={a} idx={idx + 1} />)}
                    </div>
                  </div>
                ))}
                {unscheduled.length > 0 && (
                  <div className="space-y-3" data-testid="training-day-unscheduled">
                    <div className="flex items-baseline justify-between border-b border-border pb-2">
                      <h4 className="font-bold text-lg tracking-tight">Anytime <span className="text-muted-foreground font-normal text-sm">· No fixed day</span></h4>
                      <span className="text-xs text-muted-foreground">{unscheduled.length} exercise{unscheduled.length !== 1 ? "s" : ""}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                      {unscheduled.map((a, idx) => <ExerciseCard key={a.id} a={a} idx={idx + 1} />)}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      }
      case "progress": return (
        <div className="space-y-6" data-testid="client-progress">
          {!prog ? (
            <div className="bg-card border border-border p-8 text-center">
              <h3 className="font-bold text-lg mb-2">No active program</h3>
              <p className="text-muted-foreground text-sm">Progress tracking starts once Otto builds your first program.</p>
            </div>
          ) : !baselineAssessment ? (
            <div className="bg-card border border-border p-8 text-center">
              <h3 className="font-bold text-lg mb-2">Baseline pending</h3>
              <p className="text-muted-foreground text-sm">Your baseline assessment for <span className="font-bold text-foreground">{prog.title}</span> hasn't been logged yet. Come back after your next visit.</p>
            </div>
          ) : (
            <>
              {/* Program header */}
              <div className="bg-card border border-border p-5">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#007AFF] mb-1">Current Program</p>
                <h3 className="font-black text-2xl tracking-tight">{prog.title}</h3>
                {prog.objective && <p className="text-sm text-muted-foreground mt-1">{prog.objective}</p>}
                <p className="text-xs text-muted-foreground mt-2">
                  Baseline: {formatDate(baselineAssessment.date)} · {programAssessments.length} assessment{programAssessments.length !== 1 ? "s" : ""}
                </p>
              </div>

              {/* Progress table */}
              <div className="bg-card border border-border overflow-hidden">
                <div className="px-5 py-3 border-b border-border flex items-center justify-between">
                  <h4 className="font-bold text-sm">Metric Progress</h4>
                  <span className="text-xs text-muted-foreground">Baseline → Latest → Target</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/40 border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left p-3 font-bold">Metric</th>
                        <th className="text-right p-3 font-bold">Baseline</th>
                        <th className="text-right p-3 font-bold">Latest</th>
                        <th className="text-right p-3 font-bold">Target</th>
                        <th className="text-left p-3 font-bold w-40">Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      {progressMetrics.map(pm => {
                        const hasLat = pm.baseline.left != null || pm.baseline.right != null;
                        const bVal = hasLat ? `${pm.baseline.left ?? "-"} / ${pm.baseline.right ?? "-"}` : (pm.baseline.value ?? "—");
                        const lVal = pm.latest ? (hasLat ? `${pm.latest.left ?? "-"} / ${pm.latest.right ?? "-"}` : (pm.latest.value ?? "—")) : "—";
                        const tVal = hasLat ? `${pm.target.left ?? "—"} / ${pm.target.right ?? "—"}` : (pm.target.value ?? "—");
                        // Use value (or avg of L/R) for % bar
                        const pctSingle = pctProgress(
                          pm.baseline.value ?? ((pm.baseline.left ?? 0) + (pm.baseline.right ?? 0)) / 2,
                          pm.latest?.value ?? ((pm.latest?.left ?? 0) + (pm.latest?.right ?? 0)) / 2,
                          pm.target.value ?? ((pm.target.left ?? 0) + (pm.target.right ?? 0)) / 2,
                        );
                        // 3-color scheme only: green / yellow / red (orange collapses to red).
                        const rawStatus = pm.latest?.status;
                        const normalized = rawStatus === "orange" ? "red" : rawStatus;
                        const statusColor = normalized ? { green: "#00CC52", yellow: "#EAB308", red: "#EF4444" }[normalized] : "#6b7280";
                        return (
                          <tr key={pm.name} className="border-b border-border" data-testid={`progress-row-${pm.name}`}>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: statusColor }} />
                                <span className="font-medium">{pm.name}</span>
                                {pm.unit && <span className="text-[11px] text-muted-foreground">({pm.unit})</span>}
                              </div>
                            </td>
                            <td className="p-3 text-right text-muted-foreground">{bVal}</td>
                            <td className="p-3 text-right font-bold">{lVal}</td>
                            <td className="p-3 text-right text-[#007AFF] font-bold">{tVal}</td>
                            <td className="p-3">
                              {pctSingle != null ? (
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-1.5 bg-secondary overflow-hidden">
                                    <div className="h-full bg-[#00CC52]" style={{ width: `${pctSingle}%` }} />
                                  </div>
                                  <span className="text-xs font-bold w-10 text-right">{pctSingle}%</span>
                                </div>
                              ) : <span className="text-xs text-muted-foreground">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      );
      case "schedule": return (
        <div className="space-y-6" data-testid="client-schedule">
          <h3 className="font-bold text-lg">Schedule</h3>
          <ScheduleCalendar sessions={data.upcoming_sessions || []} />
          <div>
            <h4 className="font-bold text-sm mb-3">Upcoming Sessions</h4>
            {(data.upcoming_sessions || []).length === 0 ? <p className="text-muted-foreground text-sm">No upcoming sessions.</p> : (
              <div className="space-y-3">
                {data.upcoming_sessions.map(s => (
                  <div key={s.id} className="bg-card border border-border p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div>
                        <p className="font-bold text-sm capitalize">{s.type.replace("_", " ")} Session</p>
                        <p className="text-xs text-muted-foreground">{s.notes}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-sm">{formatDate(s.date)}</p>
                      <p className="text-xs text-muted-foreground">{s.time} - {s.duration_minutes}min</p>
                      <span className="text-[10px] px-2 py-0.5 bg-[#007AFF]/10 text-[#007AFF] font-bold mt-1 inline-block">{s.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      );
      case "updates": return (
        <div className="space-y-4" data-testid="client-updates">
          <h3 className="font-bold text-lg">Progress Updates from Otto</h3>
          {clientNotes.length === 0 ? <p className="text-muted-foreground text-sm">No progress updates yet.</p> : (
            <div className="space-y-3">
              {clientNotes.map(n => (
                <div key={n.id} className="bg-card border border-border p-5">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-sm">{n.title}</h4>
                    <span className="text-xs text-muted-foreground">{formatDate(n.created_at)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{n.content}</p>
                  <p className="text-xs text-muted-foreground mt-2">— {n.author}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      );
      case "documents": return (
        <div className="space-y-4" data-testid="client-documents">
          <h3 className="font-bold text-lg">Your Documents</h3>
          {clientDocs.length === 0 ? <p className="text-muted-foreground text-sm">No documents shared yet.</p> : (
            <div className="space-y-2">
              {clientDocs.map(d => {
                const hasFile = !!d.stored_filename;
                const sizeKb = d.size_bytes ? `${(d.size_bytes / 1024).toFixed(1)} KB` : null;
                const downloadDoc = async () => {
                  try {
                    const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
                    const url = window.URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a");
                    a.href = url; a.download = d.original_filename || d.name || "download"; document.body.appendChild(a); a.click(); a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Download failed");
                  }
                };
                return (
                  <div key={d.id} className="bg-card border border-border p-4 flex items-center gap-4" data-testid={`client-doc-${d.id}`}>
                    <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center text-xs font-bold uppercase text-[#007AFF] flex-shrink-0">{d.file_type}</div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{d.name}</p>
                      <p className="text-xs text-muted-foreground">{formatDate(d.created_at)}{sizeKb && ` · ${sizeKb}`}{d.tags?.length > 0 && ` · ${d.tags.join(", ")}`}</p>
                    </div>
                    {hasFile && (
                      <Button size="sm" onClick={downloadDoc} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs flex-shrink-0" data-testid={`client-doc-download-${d.id}`}>
                        <Download className="w-3 h-3 mr-1" /> Download
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="flex h-screen bg-background text-foreground" data-testid="client-dashboard">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-border">
          <BrandMark className="h-6" />
        </div>
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            {user?.avatar ? <img src={user.avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 bg-[#007AFF] rounded-full flex items-center justify-center text-white text-sm font-bold">{user?.name?.charAt(0)}</div>}
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{user?.name}</p>
              <p className="text-[10px] text-[#007AFF] font-bold uppercase">{user?.package} Plan</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {tabs.map(t => (
            <button key={t.id} onClick={() => { setActiveTab(t.id); setSidebarOpen(false); }}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm transition-colors ${activeTab === t.id ? "bg-[#007AFF] text-white font-bold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
              data-testid={`client-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border">
          <button onClick={logout} className="w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="client-logout">
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 overflow-y-auto min-w-0">
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1"><ChevronRight className="w-5 h-5" /></button>
            <h1 className="text-lg font-bold capitalize">{tabs.find(t => t.id === activeTab)?.label}</h1>
          </div>
        </header>
        <div className="p-6">{renderContent()}</div>
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-card border border-border p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-[#007AFF]" />
        <span className="text-xs text-muted-foreground">{label}</span>
      </div>
      <p className="text-xl font-black">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function ExerciseCard({ a, idx }) {
  const ex = a.exercise || {};
  const reps = a.custom_reps || ex.reps;
  const sets = a.custom_sets || ex.sets;
  const hasVideo = !!ex.video_url;
  const [open, setOpen] = useState(false);
  const hasDetails = !!(ex.description || (ex.coaching_cues?.length > 0) || a.custom_notes);

  return (
    <div className="bg-card border border-border" data-testid={`training-exercise-${a.id}`}>
      {/* Compact row: thumb + title + meta + actions */}
      <div className="flex items-stretch">
        {/* Thumbnail */}
        <div className="relative w-20 h-20 flex-shrink-0 bg-secondary overflow-hidden">
          {ex.thumbnail ? (
            <img src={ex.thumbnail} alt={ex.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Dumbbell className="w-5 h-5 text-muted-foreground" /></div>
          )}
          {hasVideo && (
            <a
              href={ex.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 bg-black/40 hover:bg-[#007AFF]/70 flex items-center justify-center transition-colors"
              title="Watch demo video"
              data-testid={`training-video-overlay-${a.id}`}
            >
              <Play className="w-5 h-5 text-white fill-white ml-0.5" />
            </a>
          )}
        </div>
        {/* Content */}
        <div className="flex-1 min-w-0 p-2.5 pr-2">
          <div className="flex items-start gap-2">
            <span className="w-5 h-5 bg-[#007AFF] text-white text-[10px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{idx}</span>
            <div className="min-w-0 flex-1">
              <h4 className="font-bold text-sm leading-tight truncate">{ex.title || "Exercise"}</h4>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-[11px] text-muted-foreground">
                {reps && <span className="font-bold text-[#007AFF]">{reps}</span>}
                {sets && <><span className="opacity-40">·</span><span>{sets} {String(sets).toLowerCase().includes("set") ? "" : "sets"}</span></>}
                {ex.category && <><span className="opacity-40">·</span><span className="uppercase tracking-wide">{ex.category}</span></>}
              </div>
            </div>
          </div>
        </div>
        {/* Right rail actions */}
        <div className="flex flex-col items-end justify-between p-2 flex-shrink-0 gap-1">
          {hasVideo ? (
            <a
              href={ex.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-bold text-[#007AFF] hover:text-[#3395FF] transition-colors"
              title="Open video in new tab"
              data-testid={`training-video-link-${a.id}`}
            >
              <Play className="w-3 h-3 fill-current" /> Video <ExternalLink className="w-2.5 h-2.5 opacity-60" />
            </a>
          ) : <span className="text-[10px] text-muted-foreground italic">No video</span>}
          {hasDetails && (
            <button
              onClick={() => setOpen(v => !v)}
              className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-0.5"
              data-testid={`training-toggle-details-${a.id}`}
            >
              {open ? "Less" : "Details"} <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
            </button>
          )}
        </div>
      </div>
      {/* Expandable details */}
      {open && hasDetails && (
        <div className="border-t border-border px-3 py-2.5 bg-background/40 text-xs space-y-2" data-testid={`training-details-${a.id}`}>
          {ex.description && <p className="text-muted-foreground">{ex.description}</p>}
          {ex.coaching_cues?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Coaching Cues</p>
              <ul className="space-y-0.5">
                {ex.coaching_cues.map((c, i) => <li key={`${a.id}-cue-${i}`} className="text-muted-foreground flex gap-1.5"><span className="text-[#007AFF] flex-shrink-0">›</span>{c}</li>)}
              </ul>
            </div>
          )}
          {a.custom_notes && (
            <div className="bg-[#007AFF]/5 border-l-2 border-[#007AFF] px-2.5 py-1.5">
              <span className="font-bold text-[#007AFF]">Otto's note: </span><span className="text-foreground/90">{a.custom_notes}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function MilestoneRow({ milestone, formatDate }) {
  const [open, setOpen] = useState(false);
  const m = milestone;
  const hasDetail = !!(m.description || m.date);
  return (
    <div data-testid={`client-milestone-${m.id}`}>
      <button
        type="button"
        onClick={() => hasDetail && setOpen(v => !v)}
        className={`w-full text-left px-4 py-2.5 flex items-center gap-3 ${hasDetail ? "hover:bg-secondary/40 cursor-pointer" : "cursor-default"} transition-colors`}
        data-testid={`client-milestone-toggle-${m.id}`}
      >
        <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 ${m.achieved ? "bg-[#00CC52]/10" : "bg-secondary"}`}>
          <CheckCircle className={`w-3 h-3 ${m.achieved ? "text-[#00CC52]" : "text-muted-foreground"}`} />
        </div>
        <span className={`flex-1 text-sm ${m.achieved ? "font-medium" : "text-muted-foreground"}`}>{m.title}</span>
        {hasDetail && <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />}
      </button>
      {open && hasDetail && (
        <div className="px-4 pb-3 pl-12 space-y-1.5 text-xs" data-testid={`client-milestone-detail-${m.id}`}>
          {m.description && <p className="text-muted-foreground">{m.description}</p>}
          {m.date && <p className="text-muted-foreground"><span className="font-bold">Target:</span> {formatDate(m.date)}</p>}
          <p className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${m.achieved ? "bg-[#00CC52]/10 text-[#00CC52]" : "bg-secondary text-muted-foreground"}`}>
            {m.achieved ? "Achieved" : "Pending"}
          </p>
        </div>
      )}
    </div>
  );
}

